import { observable, computed, action, makeObservable, toJS } from 'mobx'

// Matches the editor's color settings — camelCase `*Color` and snake_case
// `*_color` keys (backgroundColor, contentColor, button_textColor, ...).
// Anchored to the end so unrelated keys that merely contain "color" (e.g. a
// hypothetical `colorScheme`) are never touched. Non-global, so it is safe to
// reuse across calls. Module-scoped to avoid recompiling on every serialize.
const COLOR_KEY = /color$/i

/**
 * Strips empty color values from a settings object.
 *
 * Legacy drafts can persist '' for a "no color" value, which used to fail
 * render validation (BLOCKEDITOR-X7 / FE-1168). `null` is always stripped:
 * the relaxed server pattern accepts '' but the field is still
 * `type: 'string'`, so a null must never be sent. An empty string is only
 * stripped when the default for that key is itself empty — for keys whose
 * default is a real color (e.g. the body's backgroundColor '#E1E8ED'),
 * dropping the key would resurrect the default on the next load, silently
 * undoing an explicit "no color" choice. In that case '' is kept, which the
 * server accepts since FE-1168.
 *
 * @param {object} settings - key/value pair
 * @param {object} [defaults] - the Settings defaults the values fall back to
 *   when a key is absent
 * @return {object}
 */
export const omitEmptyColors = (settings, defaults = {}) => {
  const result = {}
  Object.keys(settings).forEach((key) => {
    const value = settings[key]
    if (
      COLOR_KEY.test(key) &&
      (value === null || (value === '' && !defaults[key]))
    ) {
      return
    }
    result[key] = value
  })
  return result
}

/**
 * Mobx store that keeps settings
 *
 * @class
 */
export default class Settings {
  /** @param {object} settings - key/value pair */
  constructor(settings) {
    makeObservable(this)
    this.defaultSettings = settings
  }

  /** @type {object} keeps user defined settings */
  @observable localSettings = {}

  /**
   * Gets saved values. Local settings override default settings
   * @return {object}
   */
  @computed get values() {
    const settings = {
      ...this.defaultSettings,
      ...this.localSettings,
    }

    if (settings.headings_h1FontSize) {
      settings.headings_h2FontSize = +settings.headings_h1FontSize - 3
      settings.headings_h3FontSize = +settings.headings_h1FontSize - 6
      settings.headings_h4FontSize = +settings.headings_h1FontSize - 9
    }

    return settings
  }

  /**
   * Sets default settings
   */
  @action setDefault = (settings) => {
    if (settings) {
      this.defaultSettings = settings
    }
    this.localSettings = {}
  }

  /**
   * Sets new settings
   * @param {object} newSettings
   */
  @action set = (newSettings) => {
    this.localSettings = {
      ...this.localSettings,
      ...newSettings,
    }
  }

  /**
   * Returns local settings for persistence, with empty color values stripped
   * where absence means the same thing (see omitEmptyColors).
   * @return {object}
   */
  serialize = () =>
    omitEmptyColors(toJS(this.localSettings), this.defaultSettings)

  /**
   * Reads a single local setting value without building the full serialized
   * object, so a MobX observer that only needs this one key (e.g. a render
   * method) tracks just that key instead of every setting.
   * @param {string} key
   * @return {*}
   */
  get = (key) => this.localSettings[key]
}
